import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback, useContext, useEffect } from 'react';

import { SupportedDomains } from '@/constants/chromeExtension';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import PersonRepository from '@/repositories/PersonRepository';
import type { Person } from '@/types/person';
import RepositoryResponse from '@/types/repositoryResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const usePerson = ({
  id,
  platform,
  enabled = true
}: {
  id?: string | null;
  platform?: SupportedDomains | null;
  enabled?: boolean;
}) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const personRepository = PersonRepository();

  const handleUpdatePerson = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['getPerson', id] }),
    [id, queryClient]
  );

  const usePersonQuery = useQuery<RepositoryResponse<Person> | undefined, AxiosError>({
    enabled,
    queryKey: ['getPerson', id], // key is required for types but should be overwritten

    // Every branch below is conditional, so a platform none of them claim would
    // otherwise leave the query with no queryFn at all and React Query throws.
    // Resolving to undefined instead lets callers treat it as "no person found".
    queryFn: async () => undefined,

    // Homebase ID
    ...(!platform || platform === SupportedDomains.HOMEBASE
      ? {
          queryKey: ['getPerson', id],
          queryFn: async () => {
            if (id)
              return await personRepository.getPersonProfile({
                personId: id
              });
          }
        }
      : {}),

    // Healthie ID
    ...(platform === SupportedDomains.HEALTHIE && {
      queryKey: ['getPersonByHealthieId', id],
      queryFn: async () => {
        if (id)
          return await personRepository.getPersonProfileByHealthieId({
            healthieId: id
          });
      }
    }),

    // Kustomer ID
    ...(platform === SupportedDomains.KUSTOMER && {
      queryKey: ['getPersonByKustomerId', id],
      queryFn: async () => {
        if (id)
          return await personRepository.getPersonProfileByKustomerId({
            kustomerId: id
          });
      }
    }),

    // Candid patient ID
    ...(platform === SupportedDomains.CANDID && {
      queryKey: ['getPersonByCandidPatientId', id],
      queryFn: async () => {
        if (id)
          return await personRepository.getPersonProfileByCandidPatientId({
            candidPatientId: id
          });
      }
    })
  });

  const { data, isSuccess } = usePersonQuery;

  useEffect(() => {
    if (isSuccess && data?.data) {
      if (
        [SupportedDomains.HEALTHIE, SupportedDomains.KUSTOMER].includes(
          platform as SupportedDomains
        )
      ) {
        queryClient.setQueryData(['getPerson', data.data.id], data);
      }
    } else if (data?.isError && data?.errorMessage)
      addMessage(mapAxiosErrorMessages(data.errorMessage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccess, platform]);

  return { handleUpdatePerson, usePersonQuery };
};

export default usePerson;

export const usePersonClassifications = ({
  personId,
  enabled = true
}: {
  personId: string;
  enabled?: boolean;
}) => {
  const personRepository = PersonRepository();

  return useAPI({
    queryKey: ['getPersonClassifications', personId],
    queryFn: () => personRepository.getPersonClassifications(personId),
    enabled
  });
};
